import { useState, useEffect } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import { settingsApi } from "@/services/api";

interface NotifRule {
  id: number;
  event: string;
  module: string;
  channels: { email: boolean; inApp: boolean; whatsapp: boolean };
  roles: string[];
  enabled: boolean;
}

const initialRules: NotifRule[] = [
  { id: 1, event: "New Job Card Created", module: "Job Cards", channels: { email: true, inApp: true, whatsapp: false }, roles: ["Survey Manager", "Office Staff"], enabled: true },
  { id: 2, event: "Job Status Changed", module: "Job Cards", channels: { email: false, inApp: true, whatsapp: false }, roles: ["Survey Manager", "Field Surveyor"], enabled: true },
  { id: 3, event: "Job Deadline Approaching (3 days)", module: "Job Cards", channels: { email: true, inApp: true, whatsapp: true }, roles: ["Survey Manager", "Field Surveyor"], enabled: true },
  { id: 4, event: "Invoice Generated", module: "Billing", channels: { email: true, inApp: true, whatsapp: false }, roles: ["Survey Manager", "Office Staff"], enabled: true },
  { id: 5, event: "Payment Received", module: "Billing", channels: { email: true, inApp: true, whatsapp: true }, roles: ["Super Admin", "Survey Manager"], enabled: true },
  { id: 6, event: "Quotation Approved", module: "Billing", channels: { email: true, inApp: true, whatsapp: false }, roles: ["Survey Manager"], enabled: true },
  { id: 7, event: "Employee Check-In", module: "Field Tracking", channels: { email: false, inApp: true, whatsapp: false }, roles: ["Survey Manager"], enabled: false },
  { id: 8, event: "Deliverable Uploaded", module: "Deliverables", channels: { email: true, inApp: true, whatsapp: false }, roles: ["Survey Manager", "Office Staff"], enabled: true },
  { id: 9, event: "Deliverable Approved", module: "Deliverables", channels: { email: true, inApp: true, whatsapp: true }, roles: ["Survey Manager"], enabled: true },
  { id: 10, event: "New User Login", module: "Auth", channels: { email: false, inApp: false, whatsapp: false }, roles: ["Super Admin"], enabled: false },
  { id: 11, event: "Failed Login Attempt", module: "Auth", channels: { email: true, inApp: true, whatsapp: false }, roles: ["Super Admin"], enabled: true },
  { id: 12, event: "System Backup Completed", module: "System", channels: { email: true, inApp: false, whatsapp: false }, roles: ["Super Admin"], enabled: true },
];

interface ClientEmailRule {
  id: string;
  event: string;
  description: string;
  trigger: string;
  icon: string;
  iconColor: string;
  iconBg: string;
  enabled: boolean;
  emailSubject: string;
}

const initialClientEmailRules: ClientEmailRule[] = [
  {
    id: "job_created",
    event: "Job Card Created",
    description: "Notify client when a new job card is created and assigned to them",
    trigger: "On job card creation",
    icon: "ri-file-add-line",
    iconColor: "text-amber-600",
    iconBg: "bg-amber-50",
    enabled: true,
    emailSubject: "Your Survey Project Has Been Created — {jobNo}",
  },
  {
    id: "job_status_changed",
    event: "Job Status Changed",
    description: "Send email when job status changes (e.g. Pending → In Progress → Completed)",
    trigger: "On every status transition",
    icon: "ri-refresh-line",
    iconColor: "text-amber-600",
    iconBg: "bg-amber-50",
    enabled: true,
    emailSubject: "Project Update: {jobTitle} is now {newStatus}",
  },
  {
    id: "field_survey_started",
    event: "Field Survey Started",
    description: "Notify client when the field team checks in and begins on-site work",
    trigger: "When job moves to 'Field Survey Started'",
    icon: "ri-map-pin-user-line",
    iconColor: "text-teal-600",
    iconBg: "bg-teal-50",
    enabled: true,
    emailSubject: "Field Team Has Arrived at Your Site — {jobNo}",
  },
  {
    id: "deliverable_ready",
    event: "Deliverable Ready for Download",
    description: "Alert client when a file is uploaded and approved for download",
    trigger: "When deliverable status → Approved/Delivered",
    icon: "ri-folder-check-line",
    iconColor: "text-teal-600",
    iconBg: "bg-teal-50",
    enabled: true,
    emailSubject: "Your File Is Ready: {fileName} — {jobNo}",
  },
  {
    id: "job_completed",
    event: "Job Completed",
    description: "Send completion email with summary when all work is done",
    trigger: "When job status → Completed",
    icon: "ri-checkbox-circle-line",
    iconColor: "text-amber-600",
    iconBg: "bg-amber-50",
    enabled: true,
    emailSubject: "Project Completed — All Deliverables Ready for {jobNo}",
  },
  {
    id: "invoice_generated",
    event: "Invoice / Quotation Sent",
    description: "Email client when a new invoice or quotation is generated for their job",
    trigger: "On invoice/quotation creation",
    icon: "ri-bill-line",
    iconColor: "text-violet-600",
    iconBg: "bg-violet-50",
    enabled: true,
    emailSubject: "New {docType} from {companyName}: {invoiceNo}",
  },
  {
    id: "payment_confirmed",
    event: "Payment Confirmed",
    description: "Send payment receipt confirmation to client",
    trigger: "When invoice status → Paid",
    icon: "ri-money-rupee-circle-line",
    iconColor: "text-amber-600",
    iconBg: "bg-amber-50",
    enabled: true,
    emailSubject: "Payment Received — Thank You! ({invoiceNo})",
  },
  {
    id: "deadline_reminder",
    event: "Delivery Deadline Reminder",
    description: "Remind client 3 days before the expected delivery date",
    trigger: "3 days before job due date",
    icon: "ri-calendar-event-line",
    iconColor: "text-amber-600",
    iconBg: "bg-amber-50",
    enabled: false,
    emailSubject: "Reminder: Your Project Delivery is in 3 Days — {jobNo}",
  },
  {
    id: "site_visit_confirmed",
    event: "Site Visit Request Confirmed",
    description: "Confirm site visit appointment after admin approves the request",
    trigger: "When admin confirms a site visit booking",
    icon: "ri-map-pin-2-line",
    iconColor: "text-teal-600",
    iconBg: "bg-teal-50",
    enabled: true,
    emailSubject: "Your Site Visit is Confirmed — {visitDate}",
  },
];

const channelIcons: Record<string, string> = {
  email: "ri-mail-line",
  inApp: "ri-notification-3-line",
  whatsapp: "ri-whatsapp-line",
};

export default function AdminNotificationsConfigPage() {
  const [rules, setRules] = useState<NotifRule[]>(initialRules);
  const [clientEmailRules, setClientEmailRules] = useState<ClientEmailRule[]>(initialClientEmailRules);
  const [clientEmailMasterEnabled, setClientEmailMasterEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState<"internal" | "client">("client");
  const [successMsg, setSuccessMsg] = useState("");
  const [previewRule, setPreviewRule] = useState<ClientEmailRule | null>(null);
  const [saving, setSaving] = useState(false);

  const showSuccess = (msg: string) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), 3000); };

  // Load config from DB on mount
  useEffect(() => {
    settingsApi.getNotificationConfig()
      .then((res: { config?: { rules?: NotifRule[]; clientEmailRules?: ClientEmailRule[]; clientEmailMasterEnabled?: boolean } | null }) => {
        const cfg = res?.config;
        if (cfg) {
          if (cfg.rules) setRules(cfg.rules);
          if (cfg.clientEmailRules) setClientEmailRules(cfg.clientEmailRules);
          if (cfg.clientEmailMasterEnabled !== undefined) setClientEmailMasterEnabled(cfg.clientEmailMasterEnabled);
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await settingsApi.saveNotificationConfig({ rules, clientEmailRules, clientEmailMasterEnabled });
      showSuccess("Notification settings saved to database");
    } catch {
      showSuccess("Settings saved locally (API offline)");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = (id: number) => {
    setRules((prev) => prev.map((r) => r.id === id ? { ...r, enabled: !r.enabled } : r));
  };

  const toggleChannel = (id: number, channel: keyof NotifRule["channels"]) => {
    setRules((prev) => prev.map((r) => r.id === id ? { ...r, channels: { ...r.channels, [channel]: !r.channels[channel] } } : r));
  };

  const toggleClientRule = (id: string) => {
    setClientEmailRules((prev) => prev.map((r) => r.id === id ? { ...r, enabled: !r.enabled } : r));
  };

  const modules = Array.from(new Set(rules.map((r) => r.module)));
  const enabledClientRules = clientEmailRules.filter((r) => r.enabled).length;

  return (
    <ERPLayout title="Notification Configuration" subtitle="Control which events trigger notifications and through which channels">
      {successMsg && (
        <div className="fixed top-4 right-4 z-50 text-white text-sm px-4 py-2 rounded-xl font-medium" style={{ background: "#7c3aed" }}>
          <i className="ri-checkbox-circle-line mr-2" />{successMsg}
        </div>
      )}

      {/* Email Preview Modal */}
      {previewRule && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${previewRule.iconBg}`}>
                  <i className={`${previewRule.icon} text-lg ${previewRule.iconColor}`} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">{previewRule.event}</h3>
                  <p className="text-xs text-gray-400">Email template preview</p>
                </div>
              </div>
              <button onClick={() => setPreviewRule(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                {/* Email header */}
                <div className="bg-amber-600 px-5 py-4 text-white">
                  <p className="text-xs text-amber-200 mb-1">From: noreply@cadsoft-survey.com</p>
                  <p className="text-sm font-semibold">Subject: {previewRule.emailSubject}</p>
                </div>
                {/* Email body preview */}
                <div className="px-5 py-4 space-y-3">
                  <p className="text-sm text-gray-700">Dear <strong>[Client Name]</strong>,</p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {previewRule.id === "job_status_changed" && "We wanted to let you know that the status of your project has been updated. Please log in to your client portal to view the latest progress."}
                    {previewRule.id === "job_created" && "Your survey project has been successfully created in our system. Our team has been assigned and will begin work as scheduled."}
                    {previewRule.id === "field_survey_started" && "Our field survey team has arrived at your site and has begun the survey work. You can track real-time progress in your client portal."}
                    {previewRule.id === "deliverable_ready" && "A new file is ready for download in your client portal. Please log in to access and download your deliverable."}
                    {previewRule.id === "job_completed" && "We are pleased to inform you that your survey project has been completed. All deliverables are now available for download in your portal."}
                    {previewRule.id === "invoice_generated" && "Please find attached your invoice/quotation. You can view and download it from your client portal."}
                    {previewRule.id === "payment_confirmed" && "We have received your payment. Thank you! A receipt has been generated and is available in your portal."}
                    {previewRule.id === "deadline_reminder" && "This is a friendly reminder that your project delivery is scheduled in 3 days. Our team is on track to meet the deadline."}
                    {previewRule.id === "site_visit_confirmed" && "Your site visit request has been confirmed. Our team will be at the specified location at the agreed time."}
                  </p>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
                    <p className="text-xs text-amber-700 font-medium">View your project in the Client Portal →</p>
                  </div>
                  <p className="text-xs text-gray-400">Trigger: {previewRule.trigger}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button onClick={() => setPreviewRule(null)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-200 whitespace-nowrap">
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Internal Rules", value: rules.length, color: "#7c3aed" },
            { label: "Active Internal", value: rules.filter((r) => r.enabled).length, color: "#f59e0b" },
            { label: "Client Email Rules", value: clientEmailRules.length, color: "#0d9488" },
            { label: "Active Client Emails", value: clientEmailMasterEnabled ? enabledClientRules : 0, color: clientEmailMasterEnabled ? "#f59e0b" : "#94a3b8" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-sm text-gray-600">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {([
            { key: "client",   label: "Client Email Notifications", icon: "ri-mail-send-line" },
            { key: "internal", label: "Internal Notifications",     icon: "ri-notification-3-line" },
          ] as { key: "client" | "internal"; label: string; icon: string }[]).map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className={`${t.icon} text-sm`} /></span>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── CLIENT EMAIL NOTIFICATIONS ── */}
        {activeTab === "client" && (
          <div className="space-y-4">
            {/* Master toggle */}
            <div className={`rounded-xl border-2 p-5 transition-all ${clientEmailMasterEnabled ? "border-teal-300 bg-teal-50" : "border-gray-200 bg-gray-50"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${clientEmailMasterEnabled ? "bg-teal-100" : "bg-gray-200"}`}>
                    <i className={`ri-mail-send-line text-2xl ${clientEmailMasterEnabled ? "text-teal-600" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Client Email Notifications</p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {clientEmailMasterEnabled
                        ? `Enabled — ${enabledClientRules} of ${clientEmailRules.length} events will trigger client emails`
                        : "Disabled — No emails will be sent to clients regardless of individual settings"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${clientEmailMasterEnabled ? "bg-teal-100 text-teal-700" : "bg-gray-200 text-gray-500"}`}>
                    {clientEmailMasterEnabled ? "ON" : "OFF"}
                  </span>
                  <button
                    onClick={() => setClientEmailMasterEnabled((v) => !v)}
                    className={`relative w-14 h-7 rounded-full transition-all cursor-pointer flex-shrink-0 ${clientEmailMasterEnabled ? "bg-teal-500" : "bg-gray-300"}`}
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${clientEmailMasterEnabled ? "left-8" : "left-1"}`} />
                  </button>
                </div>
              </div>
              {!clientEmailMasterEnabled && (
                <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <i className="ri-information-line text-amber-600" />
                  <p className="text-xs text-amber-700">Client email notifications are currently <strong>disabled</strong>. Turn on the master switch above to start sending automated emails to clients.</p>
                </div>
              )}
            </div>

            {/* Per-event rules */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-gray-800">Email Trigger Rules</p>
                  <p className="text-xs text-gray-500 mt-0.5">Configure which job events send automated emails to clients</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${clientEmailMasterEnabled ? "bg-teal-50 text-teal-700" : "bg-gray-100 text-gray-400"}`}>
                  {enabledClientRules}/{clientEmailRules.length} active
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {clientEmailRules.map((rule) => (
                  <div
                    key={rule.id}
                    className={`flex items-center gap-4 px-5 py-4 transition-all ${!clientEmailMasterEnabled || !rule.enabled ? "opacity-50" : "hover:bg-gray-50"}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${rule.iconBg}`}>
                      <i className={`${rule.icon} text-lg ${rule.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-800">{rule.event}</p>
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-mono">{rule.trigger}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                      <p className="text-xs text-gray-400 mt-1 font-mono truncate">
                        <i className="ri-mail-line mr-1" />Subject: {rule.emailSubject}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setPreviewRule(rule)}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 rounded-lg text-xs font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
                      >
                        <i className="ri-eye-line text-xs" />Preview
                      </button>
                      <button
                        onClick={() => toggleClientRule(rule.id)}
                        disabled={!clientEmailMasterEnabled}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 disabled:cursor-not-allowed ${rule.enabled && clientEmailMasterEnabled ? "bg-teal-500" : "bg-gray-200"}`}
                      >
                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${rule.enabled ? "left-6" : "left-1"}`} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SMTP config hint */}
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                  <i className="ri-settings-3-line text-amber-600" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-amber-800">SMTP Configuration Required</p>
                  <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                    To send client emails, configure your SMTP settings in the backend <code className="bg-amber-100 px-1 rounded">.env</code> file:
                    <span className="block mt-1 font-mono text-amber-800">SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASS, SMTP_FROM</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button onClick={saveConfig} disabled={saving}
                className="flex items-center gap-2 text-white px-6 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap bg-teal-600 hover:bg-teal-700 disabled:opacity-60">
                {saving ? <><i className="ri-loader-4-line animate-spin" />Saving...</> : 'Save Email Settings'}
              </button>
            </div>
          </div>
        )}

        {/* ── INTERNAL NOTIFICATIONS ── */}
        {activeTab === "internal" && (
          <div className="space-y-4">
            {/* Rules by module */}
            {modules.map((mod) => {
              const modRules = rules.filter((r) => r.module === mod);
              return (
                <div key={mod} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                    <p className="text-sm font-bold text-gray-800">{mod}</p>
                    <span className="text-xs text-gray-500">{modRules.filter((r) => r.enabled).length}/{modRules.length} active</span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {modRules.map((rule) => (
                      <div key={rule.id} className={`flex items-center gap-4 px-5 py-4 transition-all ${!rule.enabled ? "opacity-50" : ""}`}>
                        {/* Toggle */}
                        <button
                          onClick={() => toggleEnabled(rule.id)}
                          className={`relative w-10 h-5 rounded-full transition-all cursor-pointer flex-shrink-0 ${rule.enabled ? "" : "bg-gray-200"}`}
                          style={rule.enabled ? { background: "#7c3aed" } : {}}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${rule.enabled ? "left-5" : "left-0.5"}`} />
                        </button>

                        {/* Event name */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800">{rule.event}</p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {rule.roles.map((r) => (
                              <span key={r} className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{r}</span>
                            ))}
                          </div>
                        </div>

                        {/* Channel toggles */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {(["email", "inApp", "whatsapp"] as const).map((ch) => (
                            <button
                              key={ch}
                              onClick={() => toggleChannel(rule.id, ch)}
                              className={`w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-all ${rule.channels[ch] ? "text-white" : "bg-gray-100 text-gray-300 hover:bg-gray-200"}`}
                              style={rule.channels[ch] ? { background: "#7c3aed" } : {}}
                              title={ch === "inApp" ? "In-App" : ch.charAt(0).toUpperCase() + ch.slice(1)}
                            >
                              <i className={`${channelIcons[ch]} text-sm`} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="flex justify-end">
              <button onClick={saveConfig} disabled={saving}
                className="flex items-center gap-2 text-white px-6 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60" style={{ background: "#7c3aed" }}>
                {saving ? <><i className="ri-loader-4-line animate-spin" />Saving...</> : 'Save All Rules'}
              </button>
            </div>
          </div>
        )}
      </div>
    </ERPLayout>
  );
}
